// 依存ゼロの ZIP 読み書き（プロジェクトのエクスポート／インポート用）。
// - 圧縮は CompressionStream('deflate-raw')。使えない環境では無圧縮（store）で書く
// - 生成物は標準の ZIP なので、OS の展開ツールや 7-Zip でそのまま中身を確認できる
// - 4GB 超（ZIP64）には未対応。1プロジェクト＝数十MB 想定のため実用上問題ない
// Node（テスト）とブラウザの両方で動く（どちらも WHATWG Streams と TextEncoder を持つ）。

const SIG_LOCAL = 0x04034b50;
const SIG_CENTRAL = 0x02014b50;
const SIG_EOCD = 0x06054b50;
const METHOD_STORE = 0;
const METHOD_DEFLATE = 8;
const UTF8_FLAG = 0x0800; // 汎用フラグ bit11: ファイル名が UTF-8

const encoder = new TextEncoder();
const decoder = new TextDecoder();

// ── CRC32 ────────────────────────────────────────────
const CRC_TABLE = (() => {
    const table = new Uint32Array(256);
    for (let i = 0; i < 256; i += 1) {
        let c = i;
        for (let k = 0; k < 8; k += 1) c = (c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1);
        table[i] = c >>> 0;
    }
    return table;
})();

export function crc32(bytes) {
    let crc = 0xFFFFFFFF;
    for (let i = 0; i < bytes.length; i += 1) crc = CRC_TABLE[(crc ^ bytes[i]) & 0xFF] ^ (crc >>> 8);
    return (crc ^ 0xFFFFFFFF) >>> 0;
}

const toBytes = (data) => (typeof data === 'string' ? encoder.encode(data) : new Uint8Array(data));

async function streamThrough(bytes, transform) {
    const stream = new Blob([bytes]).stream().pipeThrough(transform);
    const chunks = [];
    let total = 0;
    for await (const chunk of stream) {
        chunks.push(chunk);
        total += chunk.length;
    }
    const out = new Uint8Array(total);
    let offset = 0;
    for (const chunk of chunks) { out.set(chunk, offset); offset += chunk.length; }
    return out;
}

const deflateAvailable = () => typeof CompressionStream !== 'undefined';
const inflateAvailable = () => typeof DecompressionStream !== 'undefined';

async function deflateRaw(bytes) {
    if (!deflateAvailable()) return null;
    try {
        return await streamThrough(bytes, new CompressionStream('deflate-raw'));
    } catch {
        return null; // 圧縮できない環境では無圧縮で続行する（読み手は標準 ZIP として読める）
    }
}

async function inflateRaw(bytes) {
    if (!inflateAvailable()) throw new Error('この環境では圧縮された ZIP を展開できません');
    return streamThrough(bytes, new DecompressionStream('deflate-raw'));
}

// ── 書き出し ─────────────────────────────────────────
/**
 * ZIP を組み立てる。
 * @param {Array<{name: string, data: string|Uint8Array, store?: boolean}>} entries
 *        store=true で明示的に無圧縮（既に圧縮済みのバイナリ向け）
 * @param {(done: number, total: number) => void} [onProgress]
 * @returns {Promise<{parts: Uint8Array[], size: number}>} Blob へそのまま渡せる断片列
 */
export async function createZip(entries, onProgress = () => {}) {
    const parts = [];
    const central = [];
    let offset = 0;
    let index = 0;

    for (const entry of entries) {
        const nameBytes = encoder.encode(entry.name);
        const raw = toBytes(entry.data);
        const crc = crc32(raw);
        let method = METHOD_STORE;
        let body = raw;
        if (!entry.store && raw.length > 0) {
            const deflated = await deflateRaw(raw);
            // 圧縮して小さくならないファイル（GLB・PNG 等）は無圧縮のまま入れる
            if (deflated && deflated.length < raw.length) {
                method = METHOD_DEFLATE;
                body = deflated;
            }
        }

        const local = new DataView(new ArrayBuffer(30));
        local.setUint32(0, SIG_LOCAL, true);
        local.setUint16(4, 20, true);            // version needed
        local.setUint16(6, UTF8_FLAG, true);
        local.setUint16(8, method, true);
        local.setUint16(10, 0, true);            // mod time（再現性のため固定）
        local.setUint16(12, 0x0021, true);       // mod date（1980-01-01）
        local.setUint32(14, crc, true);
        local.setUint32(18, body.length, true);
        local.setUint32(22, raw.length, true);
        local.setUint16(26, nameBytes.length, true);
        local.setUint16(28, 0, true);            // extra field length
        parts.push(new Uint8Array(local.buffer), nameBytes, body);

        central.push({ nameBytes, method, crc, compressedSize: body.length, size: raw.length, offset });
        offset += 30 + nameBytes.length + body.length;
        index += 1;
        onProgress(index, entries.length);
    }

    const centralStart = offset;
    for (const c of central) {
        const head = new DataView(new ArrayBuffer(46));
        head.setUint32(0, SIG_CENTRAL, true);
        head.setUint16(4, 20, true);             // version made by
        head.setUint16(6, 20, true);             // version needed
        head.setUint16(8, UTF8_FLAG, true);
        head.setUint16(10, c.method, true);
        head.setUint16(12, 0, true);
        head.setUint16(14, 0x0021, true);
        head.setUint32(16, c.crc, true);
        head.setUint32(20, c.compressedSize, true);
        head.setUint32(24, c.size, true);
        head.setUint16(28, c.nameBytes.length, true);
        head.setUint16(30, 0, true);             // extra
        head.setUint16(32, 0, true);             // comment
        head.setUint16(34, 0, true);             // disk number
        head.setUint16(36, 0, true);             // internal attrs
        head.setUint32(38, 0, true);             // external attrs
        head.setUint32(42, c.offset, true);
        parts.push(new Uint8Array(head.buffer), c.nameBytes);
        offset += 46 + c.nameBytes.length;
    }

    const eocd = new DataView(new ArrayBuffer(22));
    eocd.setUint32(0, SIG_EOCD, true);
    eocd.setUint16(4, 0, true);
    eocd.setUint16(6, 0, true);
    eocd.setUint16(8, central.length, true);
    eocd.setUint16(10, central.length, true);
    eocd.setUint32(12, offset - centralStart, true);
    eocd.setUint32(16, centralStart, true);
    eocd.setUint16(20, 0, true);
    parts.push(new Uint8Array(eocd.buffer));

    return { parts, size: offset + 22 };
}

/** createZip の結果を 1 本の Uint8Array にする（Node・テスト用。ブラウザは Blob(parts) を使う）。 */
export function concatParts(parts) {
    const size = parts.reduce((a, p) => a + p.length, 0);
    const out = new Uint8Array(size);
    let offset = 0;
    for (const part of parts) { out.set(part, offset); offset += part.length; }
    return out;
}

// ── 読み込み ─────────────────────────────────────────
/**
 * ZIP を展開して { name: Uint8Array } のマップを返す。
 * @param {Uint8Array|ArrayBuffer} input
 * @returns {Promise<Map<string, Uint8Array>>}
 */
export async function readZip(input) {
    const bytes = input instanceof Uint8Array ? input : new Uint8Array(input);
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);

    // EOCD を末尾から探す（コメント長は最大 65535）
    let eocd = -1;
    const min = Math.max(0, bytes.length - 22 - 65535);
    for (let i = bytes.length - 22; i >= min; i -= 1) {
        if (view.getUint32(i, true) === SIG_EOCD) { eocd = i; break; }
    }
    if (eocd < 0) throw new Error('ZIP ファイルとして読み取れません（壊れているか、別の形式です）');

    const count = view.getUint16(eocd + 10, true);
    let pointer = view.getUint32(eocd + 16, true);
    const out = new Map();
    for (let i = 0; i < count; i += 1) {
        if (view.getUint32(pointer, true) !== SIG_CENTRAL) throw new Error('ZIP の索引が壊れています');
        const method = view.getUint16(pointer + 10, true);
        const crc = view.getUint32(pointer + 16, true);
        const compressedSize = view.getUint32(pointer + 20, true);
        const size = view.getUint32(pointer + 24, true);
        const nameLen = view.getUint16(pointer + 28, true);
        const extraLen = view.getUint16(pointer + 30, true);
        const commentLen = view.getUint16(pointer + 32, true);
        const localOffset = view.getUint32(pointer + 42, true);
        const name = decoder.decode(bytes.subarray(pointer + 46, pointer + 46 + nameLen));
        pointer += 46 + nameLen + extraLen + commentLen;

        if (view.getUint32(localOffset, true) !== SIG_LOCAL) throw new Error(`ZIP の内容が壊れています: ${name}`);
        const localNameLen = view.getUint16(localOffset + 26, true);
        const localExtraLen = view.getUint16(localOffset + 28, true);
        const start = localOffset + 30 + localNameLen + localExtraLen;
        const body = bytes.subarray(start, start + compressedSize);
        const data = method === METHOD_DEFLATE ? await inflateRaw(body) : body.slice();
        if (data.length !== size) throw new Error(`ZIP の展開結果が不正です: ${name}`);
        if (crc32(data) !== crc) throw new Error(`ZIP の内容が壊れています（CRC 不一致）: ${name}`);
        out.set(name, data);
    }
    return out;
}
